import React, { useState, useEffect } from "react";
import {
  useNavigate,
  useParams,
  Navigate,
  useLocation,
} from "react-router-dom";
import { toast } from "react-toastify";
import { useAuth } from "../context/AuthContext";
import styles from "./OtpVerification.module.css";

const OtpVerification = () => {
  const { isAuthenticated, verifyOtp, sendOtp, loading: isLoading } = useAuth();
  const location = useLocation();
  const isFirstTime = location.state?.isFirstTime;
  const { email, phone } = useParams();
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [resendDisabled, setResendDisabled] = useState(false);
  const [countdown, setCountdown] = useState(30);
  const navigate = useNavigate();

  // Handle countdown for resend OTP
  useEffect(() => {
    let timer;
    if (resendDisabled && countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    } else if (countdown === 0) {
      setResendDisabled(false);
    }
    return () => clearTimeout(timer);
  }, [countdown, resendDisabled]);

  const handleChange = (value, index) => {
    if (!/^\d*$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Auto focus next input
    if (value && index < 5) {
      const nextInput = document.getElementById(`otp-input-${index + 1}`);
      if (nextInput) nextInput.focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && otp[index] === "" && index > 0) {
      const prevInput = document.getElementById(`otp-input-${index - 1}`);
      if (prevInput) prevInput.focus();
    }
  };

  const handleResendOtp = async () => {
    setResendDisabled(true);
    setCountdown(30);
    const purpose = isFirstTime ? "register" : "login";
    const res = await sendOtp(
      email || phone,
      purpose,
      phone ? "phone" : "email",
    );
    if (!res.ok) {
      setResendDisabled(false);
    }
  };

  const handleOtpVerification = async (e) => {
    e.preventDefault();
    const enteredOtp = otp.join("");

    if (enteredOtp.length !== 6) {
      toast.error("Please enter a valid 6-digit OTP");
      return;
    }

    const purpose = isFirstTime ? "register" : "login";
    const res = await verifyOtp(
      email || phone,
      enteredOtp,
      purpose,
      phone ? "phone" : "email",
    );

    if (res.ok) {
      navigate("/user-dashboard");
    } else {
      setOtp(["", "", "", "", "", ""]);
      document.getElementById("otp-input-0")?.focus();
    }
  };

  if (isAuthenticated) {
    return <Navigate to="/user-dashboard" replace />;
  }

  return (
    <div className={styles.otpPageWrapper}>
      <div className={styles.otpCard}>
        <h2 className={styles.otpTitle}>OTP Verification</h2>
        <p className={styles.otpSubtitle}>
          Enter the 6-digit OTP sent to <strong>{email || phone}</strong>
        </p>

        <form onSubmit={handleOtpVerification} className={styles.otpForm}>
          <div className={styles.inputGroup}>
            {otp.map((digit, index) => (
              <input
                key={index}
                id={`otp-input-${index}`}
                type="text"
                maxLength="1"
                value={digit}
                onChange={(e) => handleChange(e.target.value, index)}
                onKeyDown={(e) => handleKeyDown(e, index)}
                className={styles.digitInput}
                disabled={isLoading}
                autoFocus={index === 0}
              />
            ))}
          </div>

          <button
            type="submit"
            className={styles.verifyButton}
            disabled={
              isLoading || otp.some((digit) => !digit) || otp.length !== 6
            }
          >
            {isLoading ? "Verifying..." : "Verify OTP"}
          </button>

          <div className={styles.footerActions}>
            <p className={styles.resendText}>
              Didn't receive the code?
              <button
                type="button"
                className={styles.resendButton}
                onClick={handleResendOtp}
                disabled={resendDisabled}
              >
                {resendDisabled ? `Resend in ${countdown}s` : "Resend OTP"}
              </button>
            </p>

            <button
              type="button"
              className={styles.backToLogin}
              onClick={() => navigate("/login")}
            >
              <i className="bi bi-arrow-left"></i> Back to Login
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default OtpVerification;
